/**
 * Gemeinsame Teile der Karten „Webinar-Einladungen" und „Direkte Bestell-Links" (P3-Review, vorschlag_v3 §2.0,
 * §2.4, §2.5): Zeilenmodell mit Sammelzeile „Ohne Kontaktzahl", Namenszelle mit Status-Etikett und Zeilen-ⓘ,
 * „—" mit Grund, Kartenkopf mit Zeitraum und Abdeckung. Beide Karten lesen dieselbe API-Antwort (Till E2-A).
 */
import { FC, ReactNode } from 'react';
import { Box, Tooltip } from '@mui/material';
import InfoTip from '../../../components/InfoTip';
import { formatBerlinWallclock } from '../../../helpers/auswertungenData';
import { GLOSSAR } from '../../../helpers/kpiDefinitions';
import { formatNumber } from '../../../helpers/numberUtils';
import { kompakt, tEuro, teilzeitraum } from './shared';
import type { FlaechenZeile, FlaechenGrauSchwellen, JeZustellung, WerbeflaechenAntwort } from '../../../helpers/auswertungenData';
import './p3Typen';

/**
 * Eine Tabellenzeile: eine Fläche, die immer offene Vergleichszeile der ganzen Liste („je zugestellter Mail") oder die
 * aufklappbare Sammelzeile „Ohne Kontaktzahl" mit ihren Flächen als Kindzeilen (in den Spalten der Karte).
 */
export type FZeile =
  | { art: 'flaeche'; id: string; z: FlaechenZeile }
  | { art: 'zustellung'; id: string; z: FlaechenZeile; je: JeZustellung }
  | { art: 'sammel'; id: string; kinder: FZeile[]; flaechen: number; anfos: number; anmeldungen: number; wertSumme: number };

/** „07.05." */
export const tm = (iso: string): string => `${iso.slice(8, 10)}.${iso.slice(5, 7)}.`;
/** „25.08.2026" */
export const datumLang = (iso: string): string => formatBerlinWallclock(iso);
/** Exklusives Fensterende → letzter gezählter Tag (YYYY-MM-DD). */
export const letzterTag = (bisExklusiv: string): string => {
  const d = new Date(`${bisExklusiv.slice(0, 10)}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() - 1);
  return d.toISOString().slice(0, 10);
};

export const anz = (n: number | null | undefined): string => (n === null || n === undefined ? '—' : formatNumber(n, 0));
export const anfosText = (n: number): string => `${anz(n)} ${n === 1 ? 'Anfo' : 'Anfos'}`;
/** Bruch 0..1 → „34,5 %". */
export const prozent = (v: number | null | undefined, stellen = 1): string =>
  v === null || v === undefined
    ? '—'
    : // toFixed(10) gegen Binärreste: 0,3445 × 100 = 34,4499… stünde sonst als „34,4 %“ da.
      `${Number((v * 100).toFixed(10)).toLocaleString('de-DE', { minimumFractionDigits: stellen, maximumFractionDigits: stellen })}\u00a0%`;
/** Dezimalzahl mit fester Stellenzahl („6,8", „0,92"). */
export const dezimal = (v: number | null | undefined, stellen: number): string =>
  v === null || v === undefined ? '—' : v.toLocaleString('de-DE', { minimumFractionDigits: stellen, maximumFractionDigits: stellen });
/** Euro mit einer Nachkommastelle („6,6 €"). */
export const euro1 = (v: number | null | undefined): string => (v === null || v === undefined ? '—' : `${dezimal(v, 1)} €`);

/** Euro-Summe in T€, eine Null aber als „0 €“ (vorschlag_v3 §2.5: „0 €“ statt „0,0 T€“). */
export const summeEuro = (v: number): string => (v === 0 ? '0\u00a0€' : tEuro(v));

/** „—" mit Grund im Tooltip (Regel 2.0/8: jedes „—" nennt seinen Grund). */
export const Strich: FC<{ grund: string }> = ({ grund }) => (
  <Tooltip title={grund} arrow placement="top">
    <Box component="span" sx={{ cursor: 'help' }}>
      —
    </Box>
  </Tooltip>
);

/** Wert mit einer kleinen Unterzeile (höchstens zwei Zeilen je Zelle, Regel 2.0/1). */
export const MitUnterzeile: FC<{ wert: ReactNode; unter?: ReactNode; fett?: boolean }> = ({ wert, unter, fett }) => (
  <Box sx={{ lineHeight: 1.25 }}>
    <Box component="span" sx={fett ? { fontWeight: 600 } : undefined}>
      {wert}
    </Box>
    {unter !== undefined && unter !== null && unter !== '' && (
      <Box component="span" sx={{ display: 'block', fontSize: 11, color: 'text.secondary', fontWeight: 400, whiteSpace: 'nowrap' }}>
        {unter}
      </Box>
    )}
  </Box>
);

/**
 * Kleiner, grauer Zusatz am Namen („wenig Anfos", „nur 23.05.–05.09."). Darf umbrechen: „nur 07.05.–11.10." hielt die
 * Namensspalte am Handy (12 Monate) sonst 2 px über die Karte (gemessen 26.10.2026).
 */
export const Zusatz: FC<{ children: ReactNode }> = ({ children }) => (
  <Box component="span" sx={{ ml: 0.75, fontSize: 11, fontWeight: 400, color: 'text.secondary', whiteSpace: 'normal' }}>
    {children}
  </Box>
);

/**
 * Status-Etikett am Namen (Vertrag §2, vorschlag_v3 §2.0/6). „letzter Versand" kommt aus dem Katalog oder — seit
 * 26.10.2026 (B72) — aus dem letzten Versand (Backend `beendet_am`). „messbar seit", wenn es Versände schon vor dem
 * Datenbeginn der Fläche gab (B68: Direkt-Mailing mit Anmelde-Link läuft seit März, erkannt erst seit 08.10.).
 */
export function statusText(z: FlaechenZeile, fensterVon: string): string | null {
  if (z.beendet_am) return `letzter Versand ${tm(z.beendet_am)}`;
  if (z.test) return z.laeuft_seit ? `Test seit ${tm(z.laeuft_seit)}` : 'Test';
  if (z.laeuft_seit && z.laeuft_seit > fensterVon) {
    const frueher = z.daten_ab !== null && z.erster_versand_gesamt !== null && z.erster_versand_gesamt < z.daten_ab;
    return `${frueher ? 'messbar' : 'läuft'} seit ${tm(z.laeuft_seit)}`;
  }
  return null;
}

const Status: FC<{ text: string }> = ({ text }) => (
  <Box
    component="span"
    sx={{
      display: 'inline-block',
      ml: 0.75,
      px: 0.5,
      fontSize: 11,
      fontWeight: 400,
      lineHeight: '16px',
      color: 'text.secondary',
      border: 1,
      borderColor: 'divider',
      borderRadius: '4px',
      // Darf umbrechen: am Handy (Namensspalte 72 px) hielt „letzter Versand 05.09.“ die Spalte sonst 126 px breit.
      whiteSpace: 'normal',
      verticalAlign: 'middle',
    }}
  >
    {text}
  </Box>
);

/** Zeilen-ⓘ: Glossar-Satz, Beschreibung, Nenner, Versandzeitraum und was sonst in keiner sichtbaren Spalte steht. */
export function zeilenInfo(z: FlaechenZeile, zusaetze: (string | null | false)[]): string {
  const glossar = z.glossar ? `${GLOSSAR[z.glossar]}.` : '';
  return [
    glossar,
    z.beschreibung,
    z.nenner ? `Kontakte: ${z.nenner}.` : '',
    z.erster_versand && z.letzter_versand ? `Versände im Zeitraum: ${datumLang(z.erster_versand)} bis ${datumLang(z.letzter_versand)}.` : '',
    ...zusaetze,
  ]
    .filter(Boolean)
    .join(' ');
}

/**
 * Namenszelle einer Fläche: Name, Status-Etikett (oder „nur TT.MM.–TT.MM.", wenn die Fläche das Fenster nicht
 * ausfüllt), Zeilen-ⓘ, Etikett des Sortiergrunds und optional eine Unterzeile (Stückzahlen ohne Kontaktzahl).
 */
export const NameZelle: FC<{
  z: FlaechenZeile;
  data: WerbeflaechenAntwort;
  info: string;
  sortGrund: string | null;
  unter?: ReactNode;
}> = ({ z, data, info, sortGrund, unter }) => {
  const status = statusText(z, data.window.von);
  const teil = status ? null : teilzeitraum(z.erster_versand, z.letzter_versand, data.window.von, data.window.versand_bis);
  return (
    // Silbentrennung (lang="de"): lange Wörter wie „Absenderadresse“ bestimmten am Handy sonst die Spaltenbreite.
    <Box component="span" lang="de" sx={{ display: 'block', lineHeight: 1.3, hyphens: 'auto' }}>
      {/* Umbruchstelle vor dem ⓘ: sonst hielt „Webinartag“+ⓘ die Namensspalte am Handy 10 px breiter (gemessen 23.10.). */}
      {z.label}
      {'\u200b'}
      <InfoTip text={info} />
      {status && <Status text={status} />}
      {teil && <Zusatz>{teil}</Zusatz>}
      {sortGrund && <Zusatz>· {sortGrund}</Zusatz>}
      {unter && (
        <Box component="span" sx={{ display: 'block', fontSize: 11, color: 'text.secondary' }}>
          {unter}
        </Box>
      )}
    </Box>
  );
};

/** Kontakte kompakt, genauer Wert im Tooltip; darunter die Versände (ganze Liste: „nur aktive", Nachfass: „Angemeldete"). */
export const KontakteZelle: FC<{ z: FlaechenZeile; mitVersaenden: boolean }> = ({ z, mitVersaenden }) => {
  if (z.kontakte === null) return <Strich grund={z.ohne_nenner_grund ?? 'Für diese Fläche gibt es keine Kontaktzahl.'} />;
  const versaende = `${anz(z.versaende)} ${z.versaende === 1 ? 'Versand' : 'Versände'}`;
  const vorsatz = z.kontakte_alle !== null ? 'nur aktive' : z.warm ? 'Angemeldete' : null;
  const unter = [vorsatz, mitVersaenden ? versaende : null].filter(Boolean).join(' · ');
  return (
    <Tooltip title={`${anz(z.kontakte)} — ${z.nenner ?? 'zugestellte Mails'}`} arrow placement="top">
      <Box component="span" sx={{ cursor: 'help', display: 'block' }}>
        <MitUnterzeile wert={kompakt(z.kontakte)} unter={unter || undefined} />
      </Box>
    </Tooltip>
  );
};

/** Grau-Grund „wenig Anfos" (Wert/Anfos je 1.000, Anfos je 100 …) — Anfos OHNE vorläufige. */
export const grauAnfos = (n: number, sw: FlaechenGrauSchwellen, etikett: string): string | null => (n < sw.anfos ? etikett : null);

/**
 * Zeilen eines Blocks: Flächen mit Kontaktzahl, dazu am Ende die Sammelzeile „Ohne Kontaktzahl" mit den übrigen als
 * Kindzeilen (`ordneKinder` legt ihre Anfangsreihenfolge fest). Flächen ohne Kontakte, Anmeldungen und Anfos liefert
 * das Backend gar nicht erst (vorschlag_v3 §2.0/7).
 */
export function zeilenAus(rows: FlaechenZeile[], ordneKinder: (a: FlaechenZeile, b: FlaechenZeile) => number): FZeile[] {
  const mit = rows.filter((z) => z.kontakte !== null);
  const ohne = rows.filter((z) => z.kontakte === null).sort(ordneKinder);
  const out: FZeile[] = mit.map((z) => ({ art: 'flaeche', id: z.key, z }));
  // Reihenfolge der Abschnitte = Reihenfolge des ersten Auftretens: erst die Flächen, dann die Sammelzeile.
  if (ohne.length > 0) {
    out.push({
      art: 'sammel',
      id: '__ohne_kontaktzahl',
      kinder: ohne.map((z) => ({ art: 'flaeche', id: z.key, z })),
      flaechen: ohne.length,
      anfos: ohne.reduce((a, z) => a + z.anfos_ohne_vorlaeufige, 0),
      anmeldungen: ohne.reduce((a, z) => a + (z.trichter?.gesamt.anmeldungen ?? 0), 0),
      wertSumme: ohne.reduce((a, z) => a + z.wert_summe, 0),
    });
  }
  return out;
}

/**
 * Ende der gezählten Anfos: der PL-X-Datenstand („25.10.2026, 22:59"), nicht das exklusive Fensterende (B50) — ohne
 * Datenstand der Stichtag.
 */
export const anfosBis = (data: WerbeflaechenAntwort): string =>
  data.datenstand ? formatBerlinWallclock(data.datenstand) : datumLang(letzterTag(data.window.bis));

/**
 * Kartenkopf (vorschlag_v3 §2.0/10). Einladungen: „Versände 27.04.–12.10.2026 · Anfos bis 25.10.2026, 22:59 · Mit
 * Kontaktzahl: …". Bestell-Links (`bestellung`): Direkt-Mailings und Nachversand zählen die Anfos zum Versand bis zum
 * Datenstand, die übrigen Flächen nach Bestelltag nur bis zum Versandschluss — der Kopf sagt beides (B62).
 */
export function kartenkopf(data: WerbeflaechenAntwort, rows: FlaechenZeile[], bestellung = false): string {
  const von = data.window.von;
  const bis = letzterTag(data.window.versand_bis);
  const versaende = von.slice(0, 4) === bis.slice(0, 4) ? `${tm(von)}–${datumLang(bis)}` : `${datumLang(von)}–${datumLang(bis)}`;
  const alle = rows.reduce((a, z) => a + z.anfos_ohne_vorlaeufige, 0);
  const mit = rows.filter((z) => z.kontakte !== null).reduce((a, z) => a + z.anfos_ohne_vorlaeufige, 0);
  const anteil = alle > 0 ? ` (${Math.round((mit / alle) * 100)} %)` : '';
  const anfos = bestellung
    ? `Anfos der Direkt-Mailings und Nachversände bis ${anfosBis(data)}, der übrigen Flächen nach Bestelltag bis ${datumLang(bis)}`
    : `Anfos bis ${anfosBis(data)}`;
  return `Versände ${versaende} · ${anfos} · Mit Kontaktzahl: ${anz(mit)} von ${anz(alle)} Anfos${anteil}`;
}

/** Fußnoten-Satz zu vorläufig ergänzten Anfos (sie zählen in keiner Quote; „+k vorläufig" nur im Tooltip). */
export function vorlaeufigHinweis(rows: FlaechenZeile[]): string {
  const n = rows.reduce((a, z) => a + z.provisional, 0);
  if (n === 0) return '';
  return ` ${anz(n)} vorläufig ergänzte ${n === 1 ? 'Anfo zählt' : 'Anfos zählen'} bei keiner Quote und keinem Wert mit (Tooltip der Zahl).`;
}
